import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { renderToStaticMarkup } from 'react-dom/server';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { X, Download, Copy, FileText, Table, Code, Image, MessageSquarePlus } from 'lucide-react';
import './FileViewerModal.css';
import htmlTemplate from '../templates/markdownHtmlTemplate';

function FileViewerModal({ isOpen, onClose, fileUrl, filename, fileType, fileSize, onAddToChat }) {
  const [fileContent, setFileContent] = useState('');
  const [fileBlob, setFileBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]); // Array of { rowIndex, colIndex, value, columnName }

  // Check if file is an image based on extension
  const isImageFile = (fname) => {
    const ext = fname?.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
  };

  useEffect(() => {
    if (isOpen && fileUrl) {
      fetchFileContent();
      setSelectedCells([]); // Reset selection when opening a new file
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fileUrl]);

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCells([]);
    }
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const fetchFileContent = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      // For image files, store as blob for proper binary download
      if (isImageFile(filename)) {
        const blob = await response.blob();
        setFileBlob(blob);
        setFileContent(''); // Clear text content for images
      } else {
        const content = await response.text();
        setFileContent(content);
        setFileBlob(null);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching file content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fileContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleDownload = () => {
    // For image files, use the stored blob to preserve binary data
    if (isImageFile(filename) && fileBlob) {
      const url = URL.createObjectURL(fileBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // For text files, create blob from text content
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadAsHTML = () => {
    // Convert markdown to HTML with proper styling
    const htmlContent = convertMarkdownToHTML(fileContent, filename);

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Replace .md extension with .html
    const htmlFilename = filename.replace(/\.(md|markdown)$/i, '.html');
    a.download = htmlFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const convertMarkdownToHTML = (markdown, title) => {
    // Render ReactMarkdown to static HTML with GFM support
    const markdownHTML = renderToStaticMarkup(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    );

    // Replace placeholders in the template
    const finalHTML = htmlTemplate
      .replace('{{TITLE}}', title)
      .replace('{{CONTENT}}', markdownHTML);

    return finalHTML;
  };

  const getFileIcon = () => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
      return <Image size={20} />;
    } else if (['csv'].includes(ext)) {
      return <Table size={20} />;
    } else if (['md', 'markdown'].includes(ext)) {
      return <FileText size={20} />;
    } else if (['html', 'htm'].includes(ext)) {
      return <Code size={20} />;
    } else {
      return <FileText size={20} />;
    }
  };

  const getLanguage = () => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'py':
        return 'python';
      case 'js':
        return 'javascript';
      case 'json':
        return 'json';
      case 'html':
      case 'htm':
        return 'html';
      case 'css':
        return 'css';
      case 'csv':
        return 'csv';
      case 'xml':
        return 'xml';
      case 'yaml':
      case 'yml':
        return 'yaml';
      case 'sql':
        return 'sql';
      default:
        return 'text';
    }
  };

  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const rows = lines.slice(1).map(line =>
      line.split(',').map(cell => cell.trim().replace(/"/g, ''))
    );

    return { headers, rows };
  };

  const handleCellClick = (rowIndex, colIndex, value, columnName) => {
    setSelectedCells(prev => {
      const existingIndex = prev.findIndex(
        cell => cell.rowIndex === rowIndex && cell.colIndex === colIndex
      );

      if (existingIndex >= 0) {
        // Remove if already selected (toggle off)
        return prev.filter((_, idx) => idx !== existingIndex);
      } else {
        // Add to selection
        return [...prev, { rowIndex, colIndex, value, columnName }];
      }
    });
  };

  const handleRemoveSelection = (rowIndex, colIndex) => {
    setSelectedCells(prev =>
      prev.filter(cell => !(cell.rowIndex === rowIndex && cell.colIndex === colIndex))
    );
  };

  const handleClearAllSelections = () => {
    setSelectedCells([]);
  };

  const handleAddToChat = () => {
    if (selectedCells.length > 0 && onAddToChat) {
      onAddToChat({
        filename,
        selections: selectedCells.map(cell => ({
          columnName: cell.columnName,
          value: cell.value
        }))
      });
      setSelectedCells([]);
      onClose();
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="file-viewer-loading">
          <div className="loading-spinner"></div>
          <p>Loading file content...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="file-viewer-error">
          <p>Error loading file: {error}</p>
          <button onClick={fetchFileContent} className="retry-button">
            Retry
          </button>
        </div>
      );
    }

    const ext = filename?.split('.').pop()?.toLowerCase();

    // Handle markdown files
    if (['md', 'markdown'].includes(ext)) {
      return (
        <div className="markdown-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{fileContent}</ReactMarkdown>
        </div>
      );
    }

    // Handle CSV files
    if (ext === 'csv') {
      const { headers, rows } = parseCSV(fileContent);
      return (
        <div className="csv-content">
          {selectedCells.length > 0 && onAddToChat && (
            <div className="csv-selection-bar">
              <div className="csv-selection-info">
                <span className="csv-selection-label">Selected ({selectedCells.length}):</span>
                <div className="csv-selection-chips">
                  {selectedCells.map((cell, idx) => (
                    <span
                      key={`${cell.rowIndex}-${cell.colIndex}`}
                      className="csv-selection-chip"
                      onClick={() => handleRemoveSelection(cell.rowIndex, cell.colIndex)}
                      title="Click to remove"
                    >
                      <span className="csv-chip-column">{cell.columnName}:</span>
                      <span className="csv-chip-value">"{cell.value}"</span>
                      <X size={12} className="csv-chip-remove" />
                    </span>
                  ))}
                </div>
                <button
                  onClick={handleClearAllSelections}
                  className="csv-clear-button"
                  title="Clear all selections"
                >
                  Clear All
                </button>
              </div>
              <button
                onClick={handleAddToChat}
                className="csv-add-to-chat-button"
                title="Add selected values to chat context"
              >
                <MessageSquarePlus size={16} />
                Add to Chat
              </button>
            </div>
          )}
          <div className="csv-table-container">
            <table className="csv-table">
              <thead>
                <tr>
                  {headers.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => {
                      const isSelected = selectedCells.some(
                        sc => sc.rowIndex === rowIndex && sc.colIndex === cellIndex
                      );
                      return (
                        <td
                          key={cellIndex}
                          className={`csv-cell-clickable ${isSelected ? 'csv-cell-selected' : ''}`}
                          onClick={() => handleCellClick(rowIndex, cellIndex, cell, headers[cellIndex])}
                          title={onAddToChat ? "Click to select/deselect" : undefined}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Handle HTML files
    if (['html', 'htm'].includes(ext)) {
      return (
        <div className="html-content">
          <iframe
            srcDoc={fileContent}
            title={filename}
            className="html-iframe"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      );
    }

    // Handle image files
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
      return (
        <div className="image-content">
          <img 
            src={fileUrl} 
            alt={filename}
            className="viewer-image"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div className="image-error" style={{ display: 'none' }}>
            <p>Failed to load image</p>
          </div>
        </div>
      );
    }

    // Default: syntax highlighted code
    return (
      <div className="code-content">
        <SyntaxHighlighter
          language={getLanguage()}
          style={tomorrow}
          showLineNumbers={true}
          wrapLines={true}
          customStyle={{
            margin: 0,
            borderRadius: 0,
            fontSize: '14px'
          }}
        >
          {fileContent}
        </SyntaxHighlighter>
      </div>
    );
  };

  if (!isOpen) return null;

  // Check if current file is markdown or image
  const ext = filename?.split('.').pop()?.toLowerCase();
  const isMarkdown = ['md', 'markdown'].includes(ext);
  const isImage = isImageFile(filename);
  const isCsv = ext === 'csv';

  // Get CSV stats for header display
  const getCsvStats = () => {
    if (!isCsv || !fileContent) return null;
    const { headers, rows } = parseCSV(fileContent);
    return { rows: rows.length, cols: headers.length };
  };
  const csvStats = getCsvStats();

  return (
    <div className="file-viewer-overlay" onClick={onClose}>
      <div className="file-viewer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="file-viewer-header">
          <div className="file-info">
            {getFileIcon()}
            <div className="file-details">
              <h3 className="file-title">{filename}</h3>
              <span className="file-meta">
                {csvStats && `${csvStats.rows} rows · ${csvStats.cols} cols · `}
                {fileSize ? `${(fileSize / 1024).toFixed(1)} KB` : ''}
              </span>
            </div>
          </div>
          <div className="file-actions">
            {/* Hide copy button for image files since binary data can't be copied as text */}
            {!isImage && (
              <button
                onClick={handleCopy}
                className="action-button file-copy-button"
                title="Copy content"
              >
                <Copy size={16} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
            <button
              onClick={handleDownload}
              className="action-button"
              title="Download file"
            >
              <Download size={16} />
              Download
            </button>
            {isMarkdown && (
              <button
                onClick={handleDownloadAsHTML}
                className="action-button html-download-button"
                title="Download as HTML"
              >
                <Code size={16} />
                HTML
              </button>
            )}
            <button
              onClick={onClose}
              className="close-button"
              title="Close viewer"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="file-viewer-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default FileViewerModal;
